import { useEffect, useRef, useState } from 'react';

const SCRIPT_ID = 'cf-turnstile-api';
const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

function pageTheme() {
  const root = document.documentElement;
  const body = document.body;
  if (
    root.getAttribute('data-vf-theme') === 'dark' ||
    root.getAttribute('data-theme') === 'dark' ||
    root.classList.contains('vf-theme-dark') ||
    root.classList.contains('dark') ||
    body?.classList.contains('vf-theme-dark') ||
    body?.classList.contains('dark')
  ) {
    return 'dark';
  }
  return 'light';
}

function loadScript() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  const script =
    document.getElementById(SCRIPT_ID) ||
    document.head.appendChild(
      Object.assign(document.createElement('script'), {
        id: SCRIPT_ID,
        src: SCRIPT_SRC,
        async: true,
        defer: true,
      })
    );
  return new Promise((resolve, reject) => {
    script.addEventListener('load', () => resolve(window.turnstile), {
      once: true,
    });
    script.addEventListener(
      'error',
      () => reject(new Error('Failed to load Turnstile')),
      { once: true }
    );
  });
}

export default function TurnstileWidget({
  siteKey,
  language = 'en',
  onVerified,
  widgetKey,
}) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onVerifiedRef = useRef(onVerified);
  const [theme, setTheme] = useState(pageTheme);

  useEffect(() => {
    onVerifiedRef.current = onVerified;
  }, [onVerified]);

  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(pageTheme()));
    const onThemeChange = (event) =>
      setTheme(event.detail === 'dark' ? 'dark' : pageTheme());
    window.addEventListener('vf-theme-change', onThemeChange);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-vf-theme', 'style'],
    });
    if (document.body)
      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ['class', 'data-vf-theme'],
      });
    return () => {
      observer.disconnect();
      window.removeEventListener('vf-theme-change', onThemeChange);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const notify = (token) => onVerifiedRef.current?.(token);
    notify(null);
    loadScript()
      .then((turnstile) => {
        if (cancelled || !turnstile || !containerRef.current || !siteKey)
          return;
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          language,
          // Turnstile's content is in a cross-origin iframe. Recreate it
          // with the provider's supported theme whenever the page changes.
          theme: theme === 'dark' ? 'dark' : 'light',
          appearance: 'always',
          callback: notify,
          'expired-callback': () => notify(null),
          'error-callback': () => notify(null),
        });
      })
      .catch(() => notify(null));
    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current !== null)
        window.turnstile.remove(widgetIdRef.current);
      widgetIdRef.current = null;
    };
  }, [siteKey, language, theme, widgetKey]);

  return (
    <div className="flex justify-center vf-turnstile-wrap" data-theme={theme}>
      <div ref={containerRef} />
    </div>
  );
}
